import { Box, Text, Tooltip } from '@zillow/constellation-10';
import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';

import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import numberUtils from '../../../../utils/numberUtils';

import ConnectedNetworkView from '../../../../components/ConnectedNetworkView';
import PropertyConstants from '../../../../constants/PropertyConstants';

import {
  StyledFlexPriceOnlyContainer,
  StyledMobileOnlyVerticalDivider,
} from '../../../../components/ConnectedListingFooter/components/AdditionalInformation/StyledComponents';

const supportedFeedConnectRates = [
  AdproductsConstants.ProductCodes.SINGLE_FAMILY,
  AdproductsConstants.ProductCodes.SINGLE_FAMILY_DAILY_LISTINGS,
];

const { SYNDICATION_OVERVIEW_STATUS } = PropertyConstants;

type ContractDetails = {
  price: number | null;
  unitOfMeasure: string | null;
  productType: string | null;
  unitCost?: number;
};

type Props = {
  hidePrice: boolean;
  contractDetails?: ContractDetails;
};

const unitOfMeasureMapping: Record<string, string> = {
  listings: 'month',
  days: 'day',
};

const FeedConnectAdditionalInformation = ({ hidePrice }: Props): JSX.Element | null => {
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const isMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));

  const {
    properties: {
      currentProperty: {
        isMultifamily,
        singleFamilyContract: { isActive: isContractActive, details: contractDetails },
        syndication,
        syndication: {
          overview: { status },
        },
      },
    },
  } = useSelector((state) => state);

  const { unitCost, unitOfMeasure, productType } = contractDetails;
  const formattedPrice = numberUtils.asCurrencyWithDefault(unitCost, '-');

  let priceText: string | null = null;

  if (unitOfMeasureMapping[unitOfMeasure] === 'day') {
    const chargeDetails = isMultifamily ? ' per unit' : '';
    priceText = `If you publish this listing, you'll be billed ${formattedPrice} per day ${chargeDetails},
            the rate in your Feed Connect™ contract.`;
  } else if (unitOfMeasureMapping[unitOfMeasure] === 'month') {
    const chargeDetails = isMultifamily ? 'per unit for any units' : 'per average listing';
    priceText = `If you publish this listing, it will be added to your monthly invoice where you are charged
            ${formattedPrice}/month ${chargeDetails} live in the month.`;
  }

  const renderPriceComponent = (): JSX.Element | null => {
    if (
      !unitCost ||
      !isContractActive ||
      !productType ||
      hidePrice ||
      supportedFeedConnectRates.indexOf(productType) === -1
    ) {
      return null;
    }
    return (
      <StyledFlexPriceOnlyContainer>
        <Box css={{ display: 'flex' }}>
          <Text textStyle="body-sm-bold" css={{ fontSize: { lg: 'fontSizes.heading-xs' } }}>
            {formattedPrice}
          </Text>
          &nbsp;
          <Text asChild textStyle="body-xs" css={{ fontSize: { lg: 'fontSizes.body-sm' }, lineHeight: { lg: '30px' } }}>
            <span>{`/${unitOfMeasureMapping[unitOfMeasure]}`}</span>
          </Text>
          &nbsp;
          <Tooltip.Root placement="top">
            <Tooltip.Trigger />
            <Tooltip.Portal>
              <Tooltip.Content>
                <Text textStyle="body-sm">{priceText}</Text>
                <Tooltip.Arrow />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Box>
        <StyledMobileOnlyVerticalDivider />
      </StyledFlexPriceOnlyContainer>
    );
  };

  if (status === SYNDICATION_OVERVIEW_STATUS.PARTIALLY_PUBLISHED) {
    return (
      <Box css={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
        <ConnectedNetworkView syndication={syndication} label={`View published ${isMobile ? '' : 'listings'}`} />
        {isScreenDesktop && renderPriceComponent()}
      </Box>
    );
  }

  if (status === SYNDICATION_OVERVIEW_STATUS.DECLINED) {
    return renderPriceComponent();
  }

  if (status === SYNDICATION_OVERVIEW_STATUS.INACTIVE) {
    return renderPriceComponent();
  }

  if (status === SYNDICATION_OVERVIEW_STATUS.PUBLISHED && !isMobile) {
    return renderPriceComponent();
  }

  if (status === SYNDICATION_OVERVIEW_STATUS.PUBLISHING && !isMobile) {
    return renderPriceComponent();
  }

  return null;
};

export default FeedConnectAdditionalInformation;
